import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Formik } from 'formik';
import type { FC } from 'react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { projectApi } from 'src/api/projectApi';
import { userApi } from 'src/api/userApi';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import logger from 'src/utils/logger';

interface MissingProject {
  project_id: number;
  project_order_number: string;
  name: string;
}

interface Props {
  open: boolean;
  projects: MissingProject[];
  onClose: () => void;
}

const useStyles = makeStyles((theme) => ({
  textField: {
    borderRadius: 4,
  },
  readOnly: {
    borderRadius: '4px !important',
    background: '#efefef !important',
  },
  projectSection: {
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '&:last-child': {
      borderBottom: 'none',
      marginBottom: 0,
    },
  },
  submitButton: {
    borderRadius: 4,
    textTransform: 'unset',
    width: 200,
    height: 45,
  },
  remindButton: {
    borderRadius: 4,
    textTransform: 'unset',
    width: 200,
    height: 45,
    background: '#F5F5F5',
    color: '#263238',
  },
  buttonsWrapper: {
    marginRight: '1.7rem',
    marginBottom: '1rem',
    borderTop: '2px solid #D8D8D8',
    marginLeft: '1.7rem',
    paddingTop: '28px',
  },
  loadingCircle: {
    color: 'white',
    width: '20px !important',
    height: '20px !important',
    marginLeft: 10,
  },
}));

// ── Per-project row with independent autocomplete state ──────────────────────

interface RowProps {
  project: MissingProject;
  index: number;
  clientId: number;
  classes: ReturnType<typeof useStyles>;
  setFieldValue: (field: string, value: any) => void;
  touched: any;
  errors: any;
  t: (key: string) => string;
}

const ProjectContactRow: FC<RowProps> = ({
  project, index, clientId, classes, setFieldValue, touched, errors, t,
}) => {
  const [contactValue, setContactValue] = useState('');
  const [contactOpen, setContactOpen] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);

  const handleContactAutoComplete = (_e, item) => {
    setFieldValue(`projects[${index}].main_client_contact_id`, item?.value ?? 0);
  };

  const handleContactAutoCompleteSuggestion = async (e: any) => {
    setContactLoading(true);
    try {
      const res: any = await userApi.getAllUserAutoComplete(clientId, e?.target?.value || '');
      setContactOptions(res?.data);
      setContactLoading(false);
    } catch (err) {
      setContactOptions([]);
      setContactLoading(false);
    }
  };

  return (
    <>
      <Grid
        item
        md={4}
        xs={12}
      >
        <TextField
          fullWidth
          label={t('projects_form_project_order_number_field')}
          value={project.project_order_number}
          variant="outlined"
          InputProps={{
            readOnly: true,
            classes: { root: classes.readOnly },
          }}
        />
      </Grid>

      <Grid
        item
        md={4}
        xs={12}
      >
        <TextField
          fullWidth
          label={t('projects_form_project_name_field')}
          value={project.name}
          variant="outlined"
          InputProps={{
            readOnly: true,
            classes: { root: classes.readOnly },
          }}
        />
      </Grid>

      <Grid
        item
        md={4}
        xs={12}
      >
        <Autocomplete
          open={contactOpen}
          onChange={(event, value) => handleContactAutoComplete(event, value)}
          onOpen={() => setContactOpen(true)}
          onClose={() => setContactOpen(false)}
          id={`main-client-contact-autocomplete-${project.project_id}`}
          options={contactOptions}
          loading={contactLoading}
          getOptionLabel={(option) => option.name || ''}
          inputValue={contactValue}
          onInputChange={(_event, newInputValue) => setContactValue(newInputValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label={t('projects_form_main_client_contact_field')}
              placeholder={t('projects_form_main_client_contact_field')}
              onChange={(e) => handleContactAutoCompleteSuggestion(e)}
              error={Boolean(
                touched.projects?.[index]?.main_client_contact_id
                && (errors.projects?.[index] as any)?.main_client_contact_id
              )}
              helperText={
                touched.projects?.[index]?.main_client_contact_id
                && (errors.projects?.[index] as any)?.main_client_contact_id
              }
              InputProps={{
                ...params.InputProps,
                classes: { root: classes.textField },
                endAdornment: (
                  <>
                    {contactLoading && (
                      <CircularProgress
                        color="inherit"
                        size={20}
                      />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Grid>
    </>
  );
};

// ── Modal ────────────────────────────────────────────────────────────────────

const MissingClientContactModal: FC<Props> = ({ open, projects, onClose }) => {
  const classes = useStyles();
  const locale = localStorage.getItem('locale');
  const { t } = useTranslation(locale);
  const clientData = JSON.parse(localStorage.getItem('client') || 'null');
  const clientId = clientData?.client_id;

  const handleRemindLater = () => {
    onClose();
  };

  return (
    <Formik
      initialValues={{
        projects: projects.map((p) => ({ project_id: p.project_id, main_client_contact_id: 0 })),
      }}
      enableReinitialize
      validationSchema={Yup.object().shape({
        projects: Yup.array().of(
          Yup.object().shape({
            main_client_contact_id: Yup.number()
              .min(1, t('projects_missing_contact_contact_required'))
              .required(t('projects_missing_contact_contact_required')),
          })
        ),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          setSubmitting(true);
          await projectApi.updateMainClientContact(values.projects);
          localStorage.removeItem('projects_missing_contact');
          localStorage.removeItem('missing_contact_last_prompted');
          toast.success(t('projects_missing_contact_save_success'));
          onClose();
        } catch (err) {
          logger(err, 'error');
          toast.error(t('projects_missing_contact_save_error'));
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ errors, isSubmitting, setFieldValue, touched, handleSubmit }) => (
        <Dialog
          open={open}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown
          onClose={(_, reason) => { if (reason !== 'backdropClick') handleRemindLater(); }}
        >
          <DialogTitle>
            {t('projects_missing_contact_modal_title')}
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: 24 }}
            >
              {t('projects_missing_contact_modal_subtitle')}
            </Typography>

            {projects.map((project, index) => (
              <Box
                key={project.project_id}
                className={classes.projectSection}
              >
                <Grid
                  container
                  spacing={3}
                >
                  <ProjectContactRow
                    project={project}
                    index={index}
                    clientId={clientId}
                    classes={classes}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    errors={errors}
                    t={t}
                  />
                </Grid>
              </Box>
            ))}
          </DialogContent>

          <DialogActions className={classes.buttonsWrapper}>
            <Button
              onClick={handleRemindLater}
              disabled={isSubmitting}
              className={classes.remindButton}
            >
              {t('projects_missing_contact_remind_later_button')}
            </Button>
            <Button
              color="secondary"
              variant="contained"
              disabled={isSubmitting}
              onClick={() => handleSubmit()}
              className={classes.submitButton}
            >
              {t('projects_missing_contact_save_button')}
              {isSubmitting && <CircularProgress className={classes.loadingCircle} />}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  );
};

export default MissingClientContactModal;
